import { useLocalStorage } from '@mantine/hooks';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCustomAppClient } from 'api/custom-client';
import { subscribeToPlanToIfcJobs } from 'app/modules/wallforming/components/copilot/hub/copilotHub';
import { useCopilotHub } from 'app/modules/wallforming/components/copilot/hub/useCopilotHub';
import { useOpenFormingSession } from 'app/modules/wallforming/components/plan-to-ifc/hooks/useOpenFormingSession';
import {
    getProblemMessages,
    readFileAsBase64
} from 'app/modules/wallforming/components/plan-to-ifc/utils/planToIfcUtils';
import { downloadBlob } from 'shared/utils/downloadBlob';
import { toastNotifications } from 'shared/utils/toastNotifications';

import type {
    GeneratedIfcModelSummary,
    OpenGeneratedIfcModelSessionResponse,
    PlanToIfcRequest,
    PlanToIfcJob
} from 'app/modules/wallforming/components/plan-to-ifc/types/planToIfcTypes';

const generatedIfcModelsQueryKey = ['generated-ifc-models'];
const terminalStatuses = new Set(['completed', 'failed', 'cancelled']);
const FALLBACK_POLL_INTERVAL_MS = 2000;

/** Pushed snapshots, POST responses and fallback reads can arrive out of order; keep the latest. */
const newerJob = (current: PlanToIfcJob | undefined, next: PlanToIfcJob) =>
    !current ||
    current.id !== next.id ||
    (!terminalStatuses.has(current.status) && Date.parse(next.lastProgressAt) >= Date.parse(current.lastProgressAt))
        ? next
        : current;

/**
 * Plan-to-IFC flow: converts PDF/image plans of one building into a stored IFC4 model with a forming session,
 * follows the background job over SignalR, and manages stored models (download, delete, open as session).
 */
export const usePlanToIfc = () => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [submissionError, setSubmissionError] = useState<{ detail: string; errors: string[] } | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [savedJob, setSavedJob] = useLocalStorage<{ id: string; autoForm: boolean } | null>({
        key: 'wallforming.plan-to-ifc.job',
        defaultValue: null,
        getInitialValueInEffect: false
    });
    const abortControllerRef = useRef<AbortController | null>(null);
    const [now, setNow] = useState(() => Date.now());

    const postPlanToIfc = useCustomAppClient<PlanToIfcJob>();
    const getJob = useCustomAppClient<PlanToIfcJob>();
    const deleteJob = useCustomAppClient<PlanToIfcJob>();
    const getGeneratedModels = useCustomAppClient<GeneratedIfcModelSummary[]>();
    const getIfcFile = useCustomAppClient<Blob>();
    const deleteIfcModel = useCustomAppClient<void>();
    const postOpenSession = useCustomAppClient<OpenGeneratedIfcModelSessionResponse>();

    const openSession = useOpenFormingSession();

    const generatedModelsQuery = useQuery({
        queryKey: generatedIfcModelsQueryKey,
        queryFn: ({ signal }) => getGeneratedModels({ url: '/generated-ifc-models', method: 'GET', signal })
    });

    const { isConnected: isLiveConnected, connectionVersion } = useCopilotHub();
    const jobQuery = useQuery({
        queryKey: ['plan-to-ifc-job', savedJob?.id],
        enabled: Boolean(savedJob?.id) && !isSubmitting,
        queryFn: async ({ signal }) => {
            const key = ['plan-to-ifc-job', savedJob!.id];
            const fetched = await getJob({
                url: `/copilot/plan-to-ifc/jobs/${savedJob!.id}`,
                method: 'GET',
                signal,
                timeout: 30000
            });
            // A read sent before a pushed update may answer after it; never go back in time.
            return newerJob(queryClient.getQueryData<PlanToIfcJob>(key), fetched);
        },
        retry: (count, error) => !(isAxiosError(error) && error.response?.status === 410) && count < 2,
        // Updates are pushed over SignalR; status is only polled as a fallback while that connection is down.
        refetchInterval: (query) =>
            isLiveConnected ||
            (query.state.data && terminalStatuses.has(query.state.data.status)) ||
            (isAxiosError(query.state.error) && query.state.error.response?.status === 410)
                ? false
                : FALLBACK_POLL_INTERVAL_MS,
        refetchIntervalInBackground: true
    });
    const job = jobQuery.data;
    const savedJobIdRef = useRef(savedJob?.id);
    useEffect(() => {
        savedJobIdRef.current = savedJob?.id;
    }, [savedJob?.id]);

    useEffect(
        () =>
            subscribeToPlanToIfcJobs((update) => {
                if (update.id === savedJobIdRef.current) {
                    queryClient.setQueryData<PlanToIfcJob>(['plan-to-ifc-job', update.id], (current) =>
                        newerJob(current, update)
                    );
                }
            }),
        [queryClient]
    );

    // Events sent while disconnected are lost, so read the current snapshot once after every (re)connect.
    const refetchJob = jobQuery.refetch;
    const hasSavedJob = Boolean(savedJob?.id);
    useEffect(() => {
        if (connectionVersion > 0 && hasSavedJob && !isSubmitting) {
            void refetchJob();
        }
    }, [connectionVersion, hasSavedJob, isSubmitting, refetchJob]);

    const isGone = isAxiosError(jobQuery.error) && jobQuery.error.response?.status === 410;
    const isGenerating = isSubmitting || Boolean(savedJob && !isGone && (!job || !terminalStatuses.has(job.status)));
    const result = job?.result ?? null;
    const pollProblem = jobQuery.error ? getProblemMessages(jobQuery.error) : null;
    const error =
        submissionError ??
        (pollProblem
            ? {
                  detail: pollProblem.detail || t('planToIfcStatusUnavailable'),
                  errors: pollProblem.errors
              }
            : job?.status === 'failed'
              ? { detail: job.error || job.progress, errors: job.errors ?? [] }
              : null);

    useEffect(() => {
        if (!isGenerating) {
            return;
        }
        const timer = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(timer);
    }, [isGenerating]);

    useEffect(() => () => abortControllerRef.current?.abort(), []);

    const generate = useCallback(
        async (
            name: string,
            files: File[],
            notes: string,
            requestOptions: { model?: string },
            autoForm: boolean
        ): Promise<void> => {
            abortControllerRef.current?.abort();
            const abortController = new AbortController();
            abortControllerRef.current = abortController;
            setIsSubmitting(true);
            setSubmissionError(null);
            try {
                const id = crypto.randomUUID();
                const request: PlanToIfcRequest = {
                    ...(requestOptions.model ? { model: requestOptions.model } : {}),
                    name: name.trim(),
                    files: await Promise.all(
                        files.map(async (file) => ({ fileName: file.name, pdfBase64: await readFileAsBase64(file) }))
                    ),
                    ...(notes.trim() ? { notes: notes.trim() } : {})
                };
                if (abortController.signal.aborted) {
                    return;
                }
                // Persist before submission so a lost acceptance response can be recovered by polling.
                setSavedJob({ id, autoForm });
                const response = await postPlanToIfc({
                    url: '/copilot/plan-to-ifc/jobs',
                    method: 'POST',
                    headers: { 'Idempotency-Key': id },
                    data: request,
                    timeout: 60000,
                    signal: abortController.signal
                });
                queryClient.setQueryData<PlanToIfcJob>(['plan-to-ifc-job', id], (current) =>
                    newerJob(current, response)
                );
            } catch (requestError: unknown) {
                if (!abortController.signal.aborted) {
                    const problem = getProblemMessages(requestError);
                    setSubmissionError({ detail: problem.detail || t('errorPlanToIfc'), errors: problem.errors });
                    if (isAxiosError(requestError) && requestError.response && requestError.response.status < 500) {
                        setSavedJob(null);
                    }
                }
            } finally {
                if (abortControllerRef.current === abortController) {
                    abortControllerRef.current = null;
                    setIsSubmitting(false);
                }
            }
        },
        [postPlanToIfc, queryClient, setSavedJob, t]
    );

    const cancel = useCallback(async () => {
        if (!savedJob) {
            abortControllerRef.current?.abort();
            return;
        }
        try {
            const response = await deleteJob({
                url: `/copilot/plan-to-ifc/jobs/${savedJob.id}`,
                method: 'DELETE',
                timeout: 30000
            });
            queryClient.setQueryData(['plan-to-ifc-job', savedJob.id], response);
            setSubmissionError(null);
        } catch (requestError: unknown) {
            const problem = getProblemMessages(requestError);
            setSubmissionError({ detail: problem.detail || t('planToIfcCancelFailed'), errors: problem.errors });
        }
    }, [deleteJob, queryClient, savedJob, t]);

    const download = useCallback(
        async (ifcModelId: string, fileName: string) => {
            try {
                const blob = await getIfcFile({
                    url: `/generated-ifc-models/${ifcModelId}/ifc`,
                    method: 'GET',
                    responseType: 'blob'
                });
                downloadBlob(blob, fileName);
            } catch (requestError: unknown) {
                toastNotifications.error({
                    title: t('error'),
                    message: getProblemMessages(requestError).detail || t('errorDownloadIfc')
                });
            }
        },
        [getIfcFile, t]
    );

    const deleteGeneratedModel = useCallback(
        async (ifcModelId: string) => {
            try {
                await deleteIfcModel({ url: `/generated-ifc-models/${ifcModelId}`, method: 'DELETE' });
                return true;
            } catch (requestError: unknown) {
                // Already deleted elsewhere: refreshing the list below is the desired outcome.
                if (!(isAxiosError(requestError) && requestError.response?.status === 404)) {
                    toastNotifications.error({
                        title: t('error'),
                        message: getProblemMessages(requestError).detail || t('errorDeleteGeneratedIfc')
                    });
                    return false;
                }
                return true;
            } finally {
                void queryClient.invalidateQueries({ queryKey: generatedIfcModelsQueryKey });
            }
        },
        [deleteIfcModel, queryClient, t]
    );

    useEffect(() => {
        if (result && savedJob) {
            void queryClient.invalidateQueries({ queryKey: generatedIfcModelsQueryKey });
            if (savedJob.autoForm && result.sessionId) {
                setSavedJob({ ...savedJob, autoForm: false });
                openSession(result.sessionId, true);
            }
        }
    }, [openSession, queryClient, result, savedJob, setSavedJob]);

    /** Stored models outlive their in-memory session, so the session is (re)created before switching. */
    const openStoredModel = useCallback(
        async (ifcModelId: string) => {
            try {
                const response = await postOpenSession({
                    url: `/generated-ifc-models/${ifcModelId}/session`,
                    method: 'POST'
                });
                openSession(response.sessionId);
                return true;
            } catch (requestError: unknown) {
                toastNotifications.error({
                    title: t('error'),
                    message: getProblemMessages(requestError).detail || t('errorOpenGeneratedIfc')
                });
                return false;
            }
        },
        [openSession, postOpenSession, t]
    );

    /** Leaves a finished or failed job so a new conversion can be started. */
    const reset = useCallback(() => {
        setSavedJob(null);
        setSubmissionError(null);
    }, [setSavedJob]);

    return {
        result,
        error,
        isGenerating,
        isSubmitting,
        job,
        elapsedSeconds: job ? Math.max(0, Math.floor((now - Date.parse(job.createdAt)) / 1000)) : 0,
        feedbackAgeSeconds: job ? Math.max(0, Math.floor((now - Date.parse(job.lastProgressAt)) / 1000)) : 0,
        generate,
        cancel,
        reset,
        download,
        deleteGeneratedModel,
        isLiveConnected,
        openSession,
        openStoredModel,
        generatedModels: Array.isArray(generatedModelsQuery.data) ? generatedModelsQuery.data : [],
        isGeneratedModelsPending: generatedModelsQuery.isPending
    };
};
